import React, { useCallback, useState } from 'react';

import { useFacetsContext } from 'components/search/Facets/FacetsProvider';
import { FacetAccordion } from 'components/search/FacetAccordion/FacetAccordion';
import useMartyContext from 'hooks/useMartyContext';
import { yieldToMain } from 'helpers/index';

export const FacetsSidebar = () => {
  const { testId } = useMartyContext();

  const {
    state: { facets, autoComplete },
    actions: { handleFacetSelect, trackFacetSearch },
    meta: { hasAutoComplete }
  } = useFacetsContext();

  // Each consumer manages its own accordion expanded state locally.
  const [expandedFacets, setExpandedFacets] = useState<Set<string>>(
    () => new Set(facets.filter(facet => facet.isExpanded).map(facet => facet.facetField))
  );

  const expandedValues = [...expandedFacets];

  // Yield before updating expanded state so the browser can paint the click
  // feedback before React mounts the expensive facet value list.
  const handleAccordionChange = useCallback(async (values: string[]) => {
    await yieldToMain();
    setExpandedFacets(new Set(values));
  }, []);

  return (
    <FacetAccordion.Root type="multiple" value={expandedValues} onValueChange={handleAccordionChange} className="max-md:px-2">
      {facets.map((facet, facetIndex) => {
        const { facetField, facetFieldDisplayName } = facet;

        return (
          <FacetAccordion.Item key={facetField} value={facetField} data-test-id={testId('facetGroup')} facet={facet} context="desktop">
            <FacetAccordion.Trigger id={facetField} data-test-id-facet-head-name={facetFieldDisplayName}>
              <FacetAccordion.DisplayName data-test-id={testId('facetGroupHeadTestId')} />
              <FacetAccordion.SelectedValueNames data-test-id={testId('facetSelectedValues')} />
            </FacetAccordion.Trigger>

            <FacetAccordion.SeoLinks />

            <FacetAccordion.Content>
              {hasAutoComplete && <FacetAccordion.AutoComplete onChange={e => trackFacetSearch(facet, e.target.value)} />}

              <FacetAccordion.ValueList data-test-id={testId(`${facetFieldDisplayName}-values`)} autoComplete={autoComplete}>
                {({ facetValue, facet: activeFacet }) => (
                  <FacetAccordion.Value
                    data-test-id={testId(`facet-${facetValue.name}`)}
                    facetValue={facetValue}
                    onFacetSelect={() => {
                      const valueIndex = activeFacet.values.findIndex(value => value.name === facetValue.name);

                      handleFacetSelect({
                        facetGroup: activeFacet.facetField,
                        facetName: facetValue.name,
                        selectedFacetGroupIndex: facetIndex,
                        selectedFacetIndex: valueIndex,
                        isSelected: facetValue?.selected || false,
                        facetValue
                      });
                    }}
                  />
                )}
              </FacetAccordion.ValueList>
            </FacetAccordion.Content>
          </FacetAccordion.Item>
        );
      })}
    </FacetAccordion.Root>
  );
};
